/* Page de structuration post-import « Examens sur table » (spec 11). Ouverte
   juste après un import réussi (ExamImportModal) — réutilise le rendu des
   sections et le panneau de chat Naïa de content-editor.tsx (DRY), avec un
   prompt de relecture/structuration plutôt que d'édition libre, et masque les
   actions sans objet ici (Publier/Aperçu/Envoyer/Générer). */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from '../lib/routing';
import { HeaderContent, APP_HEADER_HEIGHT_VAR } from '../components/shell/app-header';
import ContentSectionsEditor, { variantAwareSectionsRepository, type ContentSectionRow } from '../components/content-sections-editor';
import { EvaluationPanel } from '../components/evaluation-panel';
import { DocumentSidechat } from '../components/document-sidechat';
import { useDocumentChat } from '../hooks/use-document-chat';
import CreateExamSessionModal from '../components/create-exam-session-modal';
import VariantTabs, { type VariantDeclaration } from '../components/variant-tabs';
import { api } from '../lib/api';
import type { ContentDocumentKind } from '../lib/editor-chat';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Drawer } from '@base-ui/react';
import { CircleNotch, ListBullets, Paperclip, UploadSimple, X } from '@phosphor-icons/react';

interface Doc {
  id: string; kind: ContentDocumentKind; title: string; level_id: string; subject_id: string;
  global_correction_rules?: string | null; archived_at: string | null;
  variants?: readonly VariantDeclaration[] | null;
}
interface Ref { id: string; label: string }
interface ImportRow { id: string; storage_path: string; mime_type: string; variant: 'subject' | 'answer_key'; signed_read_url: string }

const IMPORT_VARIANT_LABEL: Record<ImportRow['variant'], string> = { subject: 'Sujet', answer_key: 'Corrigé' };

export default function ExamImportReview() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();

  const [doc, setDoc] = useState<Doc | null>(null);
  const [levels, setLevels] = useState<Ref[]>([]);
  const [subjects, setSubjects] = useState<Ref[]>([]);
  const [sections, setSections] = useState<ContentSectionRow[]>([]);
  const [imports, setImports] = useState<ImportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rubricVersion, setRubricVersion] = useState(0);
  const [attachingImport, setAttachingImport] = useState(false);
  const [showValidate, setShowValidate] = useState(false);
  const [checkingCoverage, setCheckingCoverage] = useState(false);
  /* null = variante de référence — lot 2 (spec 13). */
  const [activeVariantKey, setActiveVariantKey] = useState<string | null>(null);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  const labelOf = (list: Ref[], rid?: string) => list.find((r) => r.id === rid)?.label ?? '…';

  const loadDoc = useCallback(async () => {
    setError(null);
    try {
      const data = await api.content.get.query({ id: documentId });
      if (!data) { setError('Document introuvable.'); return; }
      setDoc(data as Doc);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [documentId]);

  const loadImports = useCallback(() => {
    api.content.imports.byDocument.query({ documentId }).then((r) => setImports(r as ImportRow[])).catch(() => {});
  }, [documentId]);

  useEffect(() => { void loadDoc(); }, [loadDoc]);
  useEffect(() => { loadImports(); }, [loadImports]);
  useEffect(() => {
    Promise.all([api.content.levels.query(), api.content.subjects.query()])
      .then(([lv, su]) => { setLevels(lv as Ref[]); setSubjects(su as Ref[]); }).catch(() => {});
  }, []);

  const chatDoc = useMemo(
    () => (doc ? { title: doc.title, level: labelOf(levels, doc.level_id), subject: labelOf(subjects, doc.subject_id), kind: doc.kind } : null),
    [doc, levels, subjects],
  );
  const {
    incomingSection, incomingCreated, incomingReordered, resourceChangedTick,
    messages, draft, setDraft, pendingImport, clearPendingImport,
    streaming, streamText, chatError, chatErrorIsPartialWrite,
    exhaustedNotice, truncatedNotice, onContinue,
    scrollRef, handleKey, send, retryLastMessage, refreshPendingImport,
  } = useDocumentChat({ documentId, doc: chatDoc, reviewMode: true, activeVariantKey });

  const attachImport = async (file: File) => {
    if (attachingImport) return;
    setAttachingImport(true);
    setError(null);
    try {
      const ext = file.name.split('.').pop() ?? 'pdf';
      const mimeType = file.type || 'application/octet-stream';
      const upload = await api.content.importAnswerKey.mutate({ documentId, attachment: { ext, mimeType } });
      const res = await fetch(upload.signed_url, { method: 'PUT', headers: { 'Content-Type': mimeType }, body: file });
      if (!res.ok) throw new Error(`Échec de l'envoi de « ${file.name} » (${res.status})`);
      refreshPendingImport();
      loadImports();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAttachingImport(false);
    }
  };

  const hasAnswerKeyImport = imports.some((i) => i.variant === 'answer_key');

  const sectionsRepo = useMemo(
    () => variantAwareSectionsRepository(documentId, activeVariantKey),
    [documentId, activeVariantKey],
  );
  const handleSectionsChange = useCallback(
    (next: ContentSectionRow[]) => { if (activeVariantKey === null) setSections(next); },
    [activeVariantKey],
  );
  const renameVariant = async (variantKey: string, label: string) => {
    try {
      await api.content.renameVariant.mutate({ documentId, variantKey, label });
      void loadDoc();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /* Gate du bouton Valider (spec 13) — le job vérifie déjà la couverture à sa
     fin, mais une édition manuelle par onglet (lot 2) peut, depuis, laisser
     une section partiellement couverte. Revérifié ici avant d'ouvrir la
     création de session. */
  const onValidateClick = async () => {
    if (!doc?.variants || doc.variants.length < 2) { setShowValidate(true); return; }
    setCheckingCoverage(true);
    setError(null);
    try {
      const check = await api.content.checkVariantCoverage.query({ documentId });
      if (!check.ok) {
        const labelOfVariant = (key: string) => doc.variants?.find((v) => v.key === key)?.label ?? key;
        setError(`Couverture de variante incomplète : une section n'a pas encore de contenu pour ${check.missing.map(labelOfVariant).join(', ')}. Corrigez-la avant de valider.`);
        return;
      }
      setShowValidate(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCheckingCoverage(false);
    }
  };

  if (loading) return (
    <div className="bg-[var(--naia-page)] flex flex-col" style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}>
      <HeaderContent width="full" showBurger backTo="/teacher/exams" backLabel="Examens sur table">{null}</HeaderContent>
      <p className="p-6 text-[var(--naia-text-secondary)]">Chargement…</p>
    </div>
  );
  if (!doc) return (
    <div className="bg-[var(--naia-page)] flex flex-col" style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}>
      <HeaderContent width="full" showBurger backTo="/teacher/exams" backLabel="Examens sur table">{null}</HeaderContent>
      <div className="p-6">
        <div className="text-[var(--naia-error)] font-bold">{error ?? 'Document introuvable.'}</div>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/teacher/exams')}>← Examens sur table</Button>
      </div>
    </div>
  );

  const chatProps = {
    messages, streaming, streamText, chatError, chatErrorIsPartialWrite, onRetry: retryLastMessage,
    exhaustedNotice, truncatedNotice, onContinue,
    draft, setDraft, handleKey, send, scrollRef,
    pendingImport, onClearPendingImport: clearPendingImport, isExam: true,
    placeholder: 'Demandez à Naïa de vérifier ou corriger la structuration…',
    emptyHint: "Naïa peut relire cet import, corriger le découpage des questions et compléter le barème.",
    /* Lot 3 (spec 13) — le chat édite désormais la variante affichée à
       l'écran ; ce badge remplace l'ancien blocage (lot 2). */
    activeBadge: activeVariantKey !== null
      ? `Naïa travaille sur ${doc.variants?.find((v) => v.key === activeVariantKey)?.label ?? activeVariantKey}`
      : null,
  };

  return (
    <div className="bg-[var(--naia-page)] flex flex-col" style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}>
      <HeaderContent
        width="full"
        showBurger
        backTo="/teacher/exams"
        backLabel="Examens sur table"
        actions={
          <>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/heic,image/webp"
                className="sr-only"
                disabled={attachingImport}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void attachImport(f);
                }}
              />
              <span className="inline-flex items-center gap-[6px] px-3 py-2 rounded-full border border-border bg-card text-[12.5px] font-semibold text-naia-blue hover:bg-naia-sky">
                {attachingImport ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} />}
                Ajouter un fichier
              </span>
            </label>
            <Button onClick={() => void onValidateClick()} disabled={checkingCoverage}>
              {checkingCoverage ? <CircleNotch size={14} className="animate-spin" /> : 'Valider'}
            </Button>
          </>
        }
      >
        {null}
      </HeaderContent>

      {error && <div className="p-[10px_20px]"><Alert>{error}</Alert></div>}

      {imports.length > 0 && (
        <div className="relative flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-[8px] bg-[var(--naia-card-alt)] border-b border-border text-[12.5px]">
          <span className="text-[var(--naia-text-secondary)] font-semibold">Fichier(s) source :</span>
          {imports.map((imp) => (
            <a key={imp.id} href={imp.signed_read_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-naia-blue font-semibold no-underline hover:underline">
              <Paperclip size={12} weight="bold" />
              {IMPORT_VARIANT_LABEL[imp.variant]}
            </a>
          ))}
          {doc.variants && doc.variants.length >= 2 && (
            <VariantTabs
              variants={doc.variants}
              activeKey={activeVariantKey}
              onSelect={setActiveVariantKey}
              onRename={(k, l) => void renameVariant(k, l)}
              className="sm:absolute sm:left-1/2 sm:-translate-x-1/2"
            />
          )}
        </div>
      )}

      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="flex-1 min-w-0 overflow-y-auto p-[20px_20px_60px]">
          <div className="max-w-[760px] mx-auto bg-[var(--naia-card)] rounded-lg shadow-sm">
            <div className="px-[32px] pt-[32px] pb-[4px]">
              <h1 className="m-0 text-[26px] font-black text-[var(--naia-text-primary)] leading-tight">{doc.title}</h1>
              <div className="flex items-center justify-end gap-[6px] mt-[6px]">
                <span className="text-[10px] text-[var(--naia-text-secondary)] opacity-60 font-medium">Évaluation</span>
                <span className="text-[10px] text-[var(--naia-text-secondary)] opacity-30">·</span>
                <span className="text-[10px] text-[var(--naia-text-secondary)] opacity-60 font-medium">{labelOf(levels, doc.level_id)} · {labelOf(subjects, doc.subject_id)}</span>
              </div>
              <div className="mt-[14px] border-b border-[var(--naia-line)]" />
            </div>
            <div className="p-[16px_32px_32px]">
              <ContentSectionsEditor
                key={rubricVersion} documentId={documentId} editable
                structureEditable={activeVariantKey === null}
                repository={sectionsRepo}
                incomingSection={activeVariantKey === null ? incomingSection : null}
                incomingCreated={activeVariantKey === null ? incomingCreated : null}
                incomingReordered={activeVariantKey === null ? incomingReordered : null}
                resourceChangedTick={activeVariantKey === null ? resourceChangedTick : undefined}
                /* Le barème (sidebar) vit sur la référence uniquement — ignorer
                   les mises à jour émises pendant qu'un onglet de variante est
                   affiché (contenu fusionné, sans bareme). */
                onSectionsChange={handleSectionsChange} />
            </div>
          </div>
        </div>

        <div className="hidden sm:flex flex-col min-h-0 min-w-0 bg-[var(--naia-card)] border-l border-[var(--naia-line)]" style={{ flexBasis: 380, flexShrink: 0 }}>
          <ImportReviewTabs
            sidechat={<DocumentSidechat {...chatProps} />}
            bareme={
              <div className="flex-1 overflow-y-auto p-4">
                {doc.variants && doc.variants.length >= 2 && (
                  <p className="m-0 mb-3 text-[12px] text-[var(--naia-text-secondary)]">
                    Barème partagé entre toutes les variantes — une seule fois, jamais dupliqué par onglet.
                  </p>
                )}
                <EvaluationPanel
                  documentId={documentId} kind={doc.kind} sections={sections}
                  initialRules={doc.global_correction_rules ?? ''} editable
                  hasAnswerKeyImport={hasAnswerKeyImport}
                  onApplied={() => { setRubricVersion((v) => v + 1); void loadDoc(); }}
                />
              </div>
            }
          />
        </div>

        <button
          type="button"
          onClick={() => setMobilePanelOpen(true)}
          aria-label="Ouvrir le barème et Naïa"
          className="sm:hidden fixed right-4 bottom-4 z-[60] w-14 h-14 grid place-items-center rounded-full bg-naia-blue text-white shadow-lg border-none cursor-pointer"
        >
          <ListBullets size={24} weight="bold" />
        </button>

        <Drawer.Root open={mobilePanelOpen} modal onOpenChange={setMobilePanelOpen} swipeDirection="right">
          <Drawer.Portal>
            <Drawer.Backdrop className="fixed inset-0 bg-[rgba(26,24,20,0.32)] z-[70] sm:hidden" />
            <Drawer.Popup
              aria-label="Barème et Naïa"
              className="fixed top-0 right-0 h-dvh w-[min(380px,92vw)] bg-[var(--naia-card)] border-l border-[var(--naia-line)] shadow-[-6px_0_0_0_rgba(26,24,20,0.08)] z-[71] flex flex-col box-border pt-[env(safe-area-inset-top)] sm:hidden"
            >
              <button
                type="button"
                onClick={() => setMobilePanelOpen(false)}
                aria-label="Fermer"
                className="self-end w-11 h-11 m-1 flex-none grid place-items-center border border-border bg-transparent rounded-[10px] cursor-pointer text-muted-foreground"
              >
                <X size={20} weight="bold" />
              </button>
              <ImportReviewTabs
                sidechat={<DocumentSidechat {...chatProps} />}
                bareme={
                  <div className="flex-1 overflow-y-auto p-4">
                    {doc.variants && doc.variants.length >= 2 && (
                      <p className="m-0 mb-3 text-[12px] text-[var(--naia-text-secondary)]">
                        Barème partagé entre toutes les variantes — une seule fois, jamais dupliqué par onglet.
                      </p>
                    )}
                    <EvaluationPanel
                      documentId={documentId} kind={doc.kind} sections={sections}
                      initialRules={doc.global_correction_rules ?? ''} editable
                      hasAnswerKeyImport={hasAnswerKeyImport}
                      onApplied={() => { setRubricVersion((v) => v + 1); void loadDoc(); }}
                    />
                  </div>
                }
              />
            </Drawer.Popup>
          </Drawer.Portal>
        </Drawer.Root>
      </div>

      {showValidate && (
        <CreateExamSessionModal
          initialDocumentId={documentId}
          onClose={() => setShowValidate(false)}
          onCreated={(sessionId) => {
            setShowValidate(false);
            navigate(`/teacher/exams/${sessionId}`);
          }}
        />
      )}
    </div>
  );
}

function ImportReviewTabs({ sidechat, bareme }: { sidechat: React.ReactNode; bareme: React.ReactNode }) {
  const [tab, setTab] = useState<'chat' | 'bareme'>('bareme');
  return (
    <>
      <div className="flex border-b border-[var(--naia-line)] bg-[var(--naia-card-alt)] flex-shrink-0">
        {(['bareme', 'chat'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`flex-1 py-[9px] text-[12px] font-bold border-b-2 transition-colors font-[inherit] bg-transparent border-x-0 border-t-0 cursor-pointer ${
              tab === t ? 'border-naia-blue text-naia-blue' : 'border-transparent text-[var(--naia-text-secondary)] hover:text-[var(--naia-text-primary)]'
            }`}
          >
            {t === 'chat' ? 'Naïa' : 'Barème'}
          </button>
        ))}
      </div>
      {tab === 'chat' ? sidechat : bareme}
    </>
  );
}
